import { useRef, useState } from 'react';
import { Image as ImageIcon, Loader2, Sparkles, X } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';

interface Props {
  open: boolean;
  /** Whether the avatar already has the daily skin cap exhausted (passed in by parent). */
  isPending?: boolean;
  onClose: () => void;
  /**
   * Caller dispatches the actual reskin mutation. Carries textPrompt + optional
   * referenceImage File. Backend uses one or the other (newer wins when both).
   */
  onGenerate: (textPrompt: string, referenceImage?: File) => void;
}

const MAX_PROMPT_CHARS = 600;

/**
 * Phase 5 — Skin marketplace modal.
 *
 * Full-screen "Choose style" sheet matching the AnimateLibraryModal UX.
 * Text prompt only in this first cut — image-style upload is the backend's
 * `imageStyleUrl` field but isn't wired in the UI yet (deferred until we
 * have a file-upload primitive in the Mini App stack).
 */
export function ReskinModal({ open, isPending, onClose, onGenerate }: Props) {
  const haptics = useHaptics();
  const [prompt, setPrompt] = useState('');
  const [refImage, setRefImage] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  if (!open) return null;

  const trimmed = prompt.trim();
  // Allow generation if we have either a prompt OR an image (Meshy needs one of them).
  const hasUsableInput = (trimmed.length >= 3 && trimmed.length <= MAX_PROMPT_CHARS) || !!refImage;
  const canGenerate = hasUsableInput && !isPending;

  const handleGenerate = () => {
    if (!canGenerate) return;
    haptics.impact('medium');
    onGenerate(trimmed, refImage ?? undefined);
  };

  const onPickRefImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setRefImage(file);
    haptics.selection();
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-bg/95 backdrop-blur">
      <header className="flex items-center justify-between px-4 py-4">
        <div>
          <h1 className="text-lg font-bold text-fg">Choose a new skin</h1>
          <p className="mt-0.5 text-xs text-fg-muted">
            Same body, new look. ~$0.20 per skin
          </p>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={() => { haptics.impact('light'); onClose(); }}
          className="rounded-full bg-bg-secondary p-2 text-fg transition-opacity active:opacity-80"
        >
          <X className="h-5 w-5" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-4 pb-32">
        <div className="flex flex-col gap-3">
          <label className="text-xs font-semibold uppercase text-fg-muted" htmlFor="reskin-prompt">
            Describe the new style
          </label>
          <textarea
            id="reskin-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            maxLength={MAX_PROMPT_CHARS}
            placeholder="Halloween pumpkin armor, glowing orange runes, spooky atmosphere"
            className="min-h-[140px] rounded-xl bg-bg-secondary p-3 text-sm text-fg placeholder:text-fg-muted/60 focus:outline-none"
          />
          <p className="text-right text-[11px] text-fg-muted">
            {trimmed.length} / {MAX_PROMPT_CHARS}
          </p>

          {/* Or: image-style reference */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase text-fg-muted">
              Or: drop a reference image
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onPickRefImage}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                'flex items-center justify-between gap-2 rounded-xl border border-dashed border-fg-muted/40 px-3 py-2.5 text-left text-xs',
                refImage ? 'bg-brand/10 text-fg' : 'bg-bg-secondary text-fg-muted',
              )}
            >
              <span className="flex items-center gap-2">
                <ImageIcon aria-hidden className="h-4 w-4" />
                {refImage ? refImage.name : 'Tap to pick a style image'}
              </span>
              {refImage && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setRefImage(null); }}
                  className="rounded-full bg-bg p-1 text-fg-muted active:opacity-80"
                  aria-label="Remove image"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </button>
            <p className="text-[11px] text-fg-muted">
              Pick a photo/render whose style you want to replicate. If both prompt + image are set, the image wins.
            </p>
          </div>

          <div className="rounded-xl bg-bg-secondary p-3 text-xs leading-relaxed text-fg-muted">
            <p className="font-semibold text-fg">Tips</p>
            <ul className="mt-1 space-y-0.5">
              <li>
                <span className="font-semibold text-fg">Skins are whole-body.</span>
                {' '}The new style applies to the entire model. Targeting just one part
                (e.g. "only the helmet") doesn't work — describe the whole avatar.
              </li>
              <li>• Specify materials (leather, gold, silk) for sharp results.</li>
              <li>• Add an atmosphere word ("spooky", "futuristic", "royal").</li>
              <li>• The mesh shape stays the same — only the surface changes.</li>
            </ul>
            <p className="mt-2 font-semibold text-fg">Good prompt examples</p>
            <ul className="mt-1 space-y-0.5">
              <li>• "Cyberpunk warrior, chrome armor, glowing neon accents"</li>
              <li>• "Medieval knight, weathered steel, dark leather straps"</li>
              <li>• "Halloween pumpkin armor, spooky orange and black"</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 border-t border-border bg-bg/95 px-4 py-3 backdrop-blur">
        <button
          type="button"
          onClick={handleGenerate}
          disabled={!canGenerate}
          className={cn(
            'flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold transition-opacity active:opacity-80',
            canGenerate ? 'bg-brand text-brand-fg' : 'bg-bg-secondary text-fg-muted',
          )}
        >
          {isPending ? (
            <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles aria-hidden className="h-4 w-4" />
          )}
          Generate skin · ~$0.20
        </button>
      </div>
    </div>
  );
}
